import { BreadcrumbSlotItem, SmartusComponentsModule } from '@affinis/smartus-components';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthService, MenuService, SettingsService } from '@core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '@shared/services/storage.service';
import { filter } from 'rxjs';

const MOBILE_MEDIAQUERY = 'screen and (max-width: 599px)';
const TABLET_MEDIAQUERY = 'screen and (min-width: 600px) and (max-width: 959px)';
const MONITOR_MEDIAQUERY = 'screen and (min-width: 960px)';
const SIDENAV_OPEN_KEY = 'sideNavOpen';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, RouterModule, SmartusComponentsModule],
})
export class AppLayoutComponent {
  options = this.settings.getOptions();

  // viewport width detection
  isTablet = false;
  isMobile = false;

  // side-nav config
  isSideNavOpen = this.storageService.getValue(SIDENAV_OPEN_KEY) ?? true;
  hasSideNav = true;
  brandingLogoXl = this.options.brandingLogoPath;
  brandingLogoXs = this.options.brandingLogoPath;
  brandingTitle = this.options.brandingTitle;
  activeNavItem = '';

  // top-bar config
  breadcrumbs: Array<BreadcrumbSlotItem> = [];
  topBarBtns = [
    {
      id: 'GU',
      label: '',
      disabled: true,
    },
    {
      id: 'Support',
      label: 'Support',
      onClick: () => {
        this.router.navigate(['/support']);
      },
      disabled: false,
      icon: 'life-ring',
    },
  ];

  accountBtns = [
    {
      id: 'Settings',
      label: this.translateService.instant('menu.settings'),
      onClick: () => {
        this.router.navigate(['/settings']);
      },
      disabled: false,
      icon: 'gear',
    },
    {
      id: 'Logout',
      label: this.translateService.instant('menu.logout'),
      onClick: () => {
        this.authService.logout();
      },
      disabled: false,
      icon: 'right-from-bracket',
    },
  ];

  constructor(
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private settings: SettingsService,
    private translateService: TranslateService,
    public authService: AuthService,
    public menuService: MenuService,
    private storageService: LocalStorageService
  ) {
    this.breakpointObserver
      .observe([MOBILE_MEDIAQUERY, TABLET_MEDIAQUERY, MONITOR_MEDIAQUERY])
      .pipe(takeUntilDestroyed())
      .subscribe(state => {
        this.isMobile = state.breakpoints[MOBILE_MEDIAQUERY];
        this.isTablet = state.breakpoints[TABLET_MEDIAQUERY];
      });
    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(e => e instanceof NavigationEnd)
      )
      .subscribe({
        next: e => {
          const event = e as NavigationEnd;
          this.activeNavItem = event.urlAfterRedirects;
          this.genBreadCrumbs(event.urlAfterRedirects);
        },
      });

    this.authService
      .getContractorName()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: name => {
          this.topBarBtns[0].label = 'GU: ' + name;
        },
      });
  }

  private genBreadCrumbs(url: string) {
    this.breadcrumbs = [];
    const sections = url.slice(1).split('/');

    for (let i = 0; i < sections.length; i++) {
      const preSection = sections[i - 1];
      const route = preSection ? [preSection, sections[i]] : [sections[i]];
      this.breadcrumbs.push({
        label: this.translateService.instant('breadcrumb.' + route.join('.')),
        onClick: () => this.router.navigate(route),
      });
    }
  }

  onSideNavItemClick(value: string) {
    this.activeNavItem = value;
    if (this.isTablet) {
      this.isSideNavOpen = false;
    }
  }

  handleBackBtnClick() {
    history.back();
  }

  handleHomeBtnClick() {
    this.router.navigate(['/']);
  }
  // side-nav functions
  handleSideNavToggle() {
    this.isSideNavOpen = !this.isSideNavOpen;
    this.storageService.set(SIDENAV_OPEN_KEY, this.isSideNavOpen);
  }

  switchTheme() {
    const btn = this.accountBtns.find(x => x.id === 'Theme');

    if (!btn) return;

    if (this.settings.isDarkTheme()) {
      btn.icon = 'sun';
      btn.label = 'LightMode';
    } else {
      btn.icon = 'moon';
      btn.label = 'DarkMode';
    }

    this.settings.switchTheme();
  }
}
